// Libraries
import { BoxProps } from '@chakra-ui/react';
import { OptionBase } from 'chakra-react-select';
import { Key } from 'react';

/**
 * Represents a basic option with a value and label.
 */
export type BasicOption = OptionBase & {
  /**
   * The value of the option.
   */
  value: string;
  /**
   * The label of the option.
   */
  label: string;
};

// TODO: Extend common properties in future additions to this component
/**
 * Represents the props for a fixed dropdown component.
 *
 * @template OptionType - The type of options in the dropdown.
 * @template IsMulti - A boolean indicating whether multiple options can be selected.
 */
export type FixedDropdownProps<OptionType extends BasicOption, IsMulti extends boolean> = Pick<
  BoxProps,
  'w' | 'key'
> & {
  /**
   * The ID of the dropdown.
   */
  id: string;
  /**
   * The name of the dropdown.
   */
  name?: string;
  /**
   * A boolean indicating whether multiple options can be selected.
   */
  isMulti: IsMulti;
  /**
   * A boolean indicating whether to add a "Select All" option in the dropdown.
   */
  addSelectAll?: boolean;
  /**
   * The message to display when there are no options available.
   */
  noOptionsMessage?: string;
  /**
   * The message to display for the "Select All" option.
   */
  selectAllMessage?: string;
  /**
   * The message to display in the search filed for the filter
   */
  placeholder?: string;
  /**
   * An array of options for the dropdown.
   */
  options: OptionType[];
  /**
   * The currently selected value or values from the dropdown. For multi-select, it's an array.
   */
  value?: (IsMulti extends true ? OptionType[] : OptionType) | null;
  /**
   * Label that shows the currently selected options
   */
  selectedLabel?: string;
  /**
   * The default selected options for the dropdown.
   */
  defaultValue?: OptionType[];
  /**
   * The callback function to be invoked when the selected options change.
   * @param {OptionType[]} selectedOptions - The selected options.
   */
  onChange: (selectedOptions: OptionType[]) => void;
};

/**
 * Represents the props for a wrapper component that includes a dropdown.
 *
 * @template OptionType - The type of options in the dropdown.
 * @template IsMulti - A boolean indicating whether multiple options can be selected.
 */
export type WrapperProps<OptionType extends BasicOption, IsMulti extends boolean> = {
  /**
   * The ID of the wrapper component.
   */
  id: string;
  /**
   * The key of the wrapper component.
   */
  key?: Key;
  /**
   * The name of the dropdown.
   */
  name?: string;
  /**
   * A boolean indicating whether multiple options can be selected.
   */
  isMulti: IsMulti;
  /**
   * The currently selected value or values from the dropdown. For multi-select, it's an array.
   */
  value?: (IsMulti extends true ? OptionType[] : OptionType) | null;
  /**
   * The message to display when there are no options available.
   */
  noOptionsMessage?: string;
  /**
   * The message to display for the "Select All" option.
   */
  selectAllMessage?: string;
  /**
   * The message to display in the search filed for the filter
   */
  placeholder?: string;
  /**
   * An array of options for the dropdown.
   */
  options: OptionType[];
  /**
   * The default selected options for the dropdown.
   */
  defaultValue?: OptionType[];
  /**
   * The callback function to be invoked when the selected options change.
   * @param {OptionType[]} selectedOptions - The selected options.
   */
  onChange?: (selectedOptions: OptionType[]) => void;
};

/**
 * Enum representing keys for select actions in a dropdown.
 */
export enum SelectKeys {
  /**
   * Key for the "Select All" action in a dropdown.
   */
  SELECT_ALL = 'select_all',
  /**
   * Key for selecting an option in a dropdown.
   */
  SELECT_OPTION = 'select-option',
  /**
   * Key for deselecting an option in a dropdown.
   */
  DESELECT_OPTION = 'deselect-option',
}
